import { useState, useMemo, useCallback, useRef } from 'react';
import { applySelectionClick } from '../utils/rangeSelection';

/**
 * Flat multi-select for an image grid (one file per card): plain click = select-one,
 * shift-click = range, ctrl/cmd-click = toggle. Wraps the framework-free
 * applySelectionClick with React state, an id-stable anchor (so a shift-range stays
 * correct when the file list re-sorts / grows / shrinks between clicks), and the
 * download-oriented derived state both observation-image pages need.
 *
 * A file is *selectable* only when it has both file_id and file_path — the folder-picker
 * download resolves raw via the /fits/download-proxy/<file_path> URL and calibrated via
 * file_id, so a row missing either can't be downloaded and must not be selectable.
 *
 * @param {Array} renderedFiles files in render order (same array the grid maps over)
 */
export function useImageSelection(renderedFiles) {
  const [state, setState] = useState({ selected: new Set(), anchorId: null });

  // Same length/order as the rendered grid; `undefined` marks a non-selectable slot so
  // applySelectionClick no-ops on it and shift-range fills skip the gap.
  const orderedIds = useMemo(
    () => (renderedFiles || []).map((f) => (f && f.file_id && f.file_path ? f.file_id : undefined)),
    [renderedFiles],
  );
  // Ref so the click handler reads the latest orderedIds without being re-created.
  const orderedRef = useRef(orderedIds);
  orderedRef.current = orderedIds;

  const handleCardClick = useCallback((index, event) => {
    const ordered = orderedRef.current;
    const modifiers = {
      shift: !!(event && event.shiftKey),
      ctrlOrMeta: !!(event && (event.ctrlKey || event.metaKey)),
    };
    setState((prev) => {
      // Resolve the anchor by id. Loose `== null` on purpose: orderedIds carries
      // `undefined` sentinels, and indexOf(undefined) would otherwise match the first
      // non-selectable card and mis-range.
      const lastIndex = prev.anchorId == null ? null : ordered.indexOf(prev.anchorId);
      const result = applySelectionClick({ selected: prev.selected, lastIndex }, index, ordered, modifiers);
      const nextAnchor = result.lastIndex != null ? ordered[result.lastIndex] : prev.anchorId;
      return { selected: result.selected, anchorId: nextAnchor == null ? null : nextAnchor };
    });
  }, []);

  const selectAll = useCallback(() => {
    const ids = orderedRef.current.filter(Boolean);
    setState({ selected: new Set(ids), anchorId: ids.length ? ids[ids.length - 1] : null });
  }, []);

  const clear = useCallback(() => setState({ selected: new Set(), anchorId: null }), []);

  // Live set of selectable ids; intersecting the raw selection with it drops any id left
  // stale by a delete/poll so it never inflates the count or a download.
  const selectableSet = useMemo(() => new Set(orderedIds.filter(Boolean)), [orderedIds]);

  const selectedIds = useMemo(() => {
    const out = new Set();
    state.selected.forEach((id) => {
      if (selectableSet.has(id)) out.add(id);
    });
    return out;
  }, [state.selected, selectableSet]);

  const calibratableSelectedIds = useMemo(() => {
    const out = new Set();
    (renderedFiles || []).forEach((f) => {
      if (f && f.calibratable && selectedIds.has(f.file_id)) out.add(f.file_id);
    });
    return out;
  }, [renderedFiles, selectedIds]);

  const isSelected = useCallback((fileId) => selectedIds.has(fileId), [selectedIds]);

  const selectableCount = selectableSet.size;
  const selectedCount = selectedIds.size;

  return {
    handleCardClick,
    isSelected,
    selectedIds,
    selectedCount,
    calibratableSelectedIds,
    calibratableCount: calibratableSelectedIds.size,
    selectableCount,
    allSelected: selectedCount === selectableCount && selectableCount > 0,
    selectAll,
    clear,
  };
}

export default useImageSelection;
